import { useEffect, useState } from 'react';
import useUserStore from '@/stores/UserStore';
import { Info, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import {
  getSelfServiceOpensSettings,
  previewOpensMessagePreview,
  saveSelfServiceOpensSettings,
} from '@/hooks/follower';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import PreviewCard from '@/components/followers/PreviewCard';

const SelfServiceSettings = () => {
  const navigate = useNavigate();
  const [promptValue, setPromptValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [isFlowActive, setIsFlowActive] = useState(false);

  const { selfServiceOpensEnabled } = useUserStore(
    useShallow((state) => ({
      selfServiceOpensEnabled: state.selfServiceOpensEnabled,
    }))
  );

  useEffect(() => {
    if (selfServiceOpensEnabled === false) {
      navigate('/inbox');
    }
  }, [selfServiceOpensEnabled, navigate]);

  useEffect(() => {
    // Load prompt from API on mount
    const loadPrompt = async () => {
      setIsLoading(true);
      try {
        const response = await getSelfServiceOpensSettings();

        if (response) {
          setPromptValue(response.data.system_prompt);
          setIsFlowActive(response.data.is_flow_active);
        } else {
          setPromptValue('');
        }
      } catch (error) {
        console.error('Error loading prompt:', error);
        toast.error('Failed to load prompt settings');
      } finally {
        setIsLoading(false);
      }
    };

    if (selfServiceOpensEnabled) {
      loadPrompt();
    } else {
      setIsLoading(false);
    }
  }, [selfServiceOpensEnabled]);

  const handleSavePrompt = async () => {
    try {
      if (promptValue.trim() === '') {
        toast.error('Please enter a prompt');
        return;
      }

      // Show preview dialog first
      setIsGeneratingPreview(true);
      setIsPreviewDialogOpen(true);

      try {
        const response = await previewOpensMessagePreview(promptValue.trim());
        if (response?.data) {
          setPreviewData(response.data);
        } else {
          toast.error('Failed to generate preview');
          setIsPreviewDialogOpen(false);
        }
      } catch (error) {
        console.error('Error generating preview:', error);
        toast.error('Failed to generate preview');
        setIsPreviewDialogOpen(false);
      } finally {
        setIsGeneratingPreview(false);
      }
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  const handleConfirmSave = async () => {
    try {
      setIsSaving(true);
      await toast.promise(saveSelfServiceOpensSettings(promptValue.trim()), {
        loading: 'Saving prompt...',
        success: () => {
          setIsSaving(false);
          setIsPreviewDialogOpen(false);
          // Update isFlowActive to true after successful save (first time)
          if (!isFlowActive) {
            setIsFlowActive(true);
          }
          return 'Prompt saved successfully';
        },
        error: (error) => {
          setIsSaving(false);
          return error.response?.data?.message || 'Failed to save prompt';
        },
      });
    } catch (error) {
      setIsSaving(false);
      toast.error('Failed to save prompt');
    }
  };

  if (!selfServiceOpensEnabled) {
    return null;
  }

  return (
    <div className='flex h-full flex-col overflow-hidden p-4'>
      <div className='mb-2'>
        <h4 className='mb-1.5 text-base font-semibold'>
          Personalized Opens Prompt
        </h4>
        <div className='space-y-1 text-xs text-muted-foreground'>
          <p>
            Configure the prompt that guides the generation of personalized
            opening messages.
          </p>
        </div>
      </div>
      <div className='flex flex-1 flex-col'>
        {/* Info Alert - Show when flow is not active */}
        {!isLoading && !isFlowActive && (
          <Alert className='mb-4 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30'>
            <Info className='h-4 w-4 text-blue-600 dark:text-blue-400' />
            <AlertDescription className='text-sm text-blue-900 dark:text-blue-200'>
              This prompt will be used for generating personalized messages.
              Please click on &quot;Save and Generate&quot; for the first time
              to activate the flow.
            </AlertDescription>
          </Alert>
        )}

        {isLoading ? (
          <div className='mb-4 flex flex-1 items-center justify-center rounded-md border'>
            <div className='flex flex-col items-center gap-2'>
              <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
              <p className='text-sm text-muted-foreground'>Loading prompt...</p>
            </div>
          </div>
        ) : (
          <Textarea
            value={promptValue}
            onChange={(e) => setPromptValue(e.target.value)}
            placeholder='Enter your personalized opens prompt here...'
            className='custom-scrollbar mb-2 min-h-[500px] resize-none'
            rows={10}
          />
        )}
        <Button
          onClick={handleSavePrompt}
          disabled={isSaving || promptValue.trim() === '' || isLoading}
          className='w-64 self-end'
        >
          {isSaving
            ? 'Saving...'
            : isFlowActive
              ? 'Save Prompt'
              : 'Save and Generate'}
        </Button>
      </div>

      {/* Preview Dialog */}
      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className='max-w-4xl'>
          <DialogHeader>
            <DialogTitle>Preview Generated Message</DialogTitle>
            <DialogDescription>
              Review how the personalized message will look with the current
              prompt.
            </DialogDescription>
          </DialogHeader>

          <div className='max-h-[70vh] overflow-y-auto'>
            {isGeneratingPreview ? (
              <div className='flex items-center justify-center py-12'>
                <div className='flex flex-col items-center gap-2'>
                  <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
                  <p className='text-sm text-muted-foreground'>
                    Generating preview...
                  </p>
                </div>
              </div>
            ) : previewData ? (
              <div className='p-4'>
                <PreviewCard
                  profileData={previewData.profile_data}
                  generatedMessage={previewData.generated_message}
                />
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setIsPreviewDialogOpen(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSave}
              disabled={isSaving || isGeneratingPreview || !previewData}
            >
              {isSaving
                ? 'Saving...'
                : isFlowActive
                  ? 'Save Prompt'
                  : 'Save and Generate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SelfServiceSettings;
